// NOAA GOES Color IR Satellite layer family.
//
// Cloud-top temperature visualization — cold tops red, warm tops
// blue/green. Useful day OR night for identifying convective tops vs.
// low cloud. 4 km native resolution.
//
// Extracted from addOverlays.ts as HI-1 batch 6b (audit 2026-05-22).
// Pure mechanical move — source URL template, paint opacity, and
// before-id stack order are byte-identical to the pre-extraction form.

import type { Map as MlMap } from 'maplibre-gl';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the GOES color-IR satellite source + raster layer. Idempotent
 * on the source.
 */
export function addSatIrLayers(map: MlMap): void {
  if (!map.getSource('satir')) {
    map.addSource('satir', {
      type: 'raster',
      // HI-7 audit 2026-05-22: see satgeocolor above for rationale.
      tiles: [
        '/api/tile?layer=goes-east-ir&z={z}&x={x}&y={y}',
      ],
      tileSize: 256,
      attribution: 'Imagery: NOAA GOES IR · Iowa State Mesonet',
      minzoom: 0,
      maxzoom: 9,
    });
    const beforeId = (() => {
      for (const id of ['sua-fill', 'tfr-fill', 'fires-fill', 'sfcanalysis-raster', 'satgeocolor-raster', 'nexrad-raster']) {
        if (map.getLayer(id)) return id;
      }
      return undefined;
    })();
    map.addLayer(
      {
        id: 'satir-raster',
        type: 'raster',
        source: 'satir',
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 0.75 },
      },
      beforeId,
    );
  }
}

registerLayerFamily({
  id: 'satir',
  visibilitySelector: (l) => l.satir,
  label: 'GOES IR satellite',
  sourceId: 'satir',
  layerIds: ['satir-raster'],
  mount: (map) => addSatIrLayers(map),
  // Restore current visibility after a basemap rebuild — see nexrad.ts.
  applyState: (map) => {
    if (!map.getLayer('satir-raster')) return;
    const show = useLayerVisibilityStore.getState().layers.satir;
    map.setLayoutProperty('satir-raster', 'visibility', show ? 'visible' : 'none');
  },
});
